import { z } from 'zod';
import { artifactManifestEntrySchema } from './artifact.ts';
import {
  blockPlacementSchema,
  dataBindingSchema,
  interactionBindingSchema,
  visualizationBlockSpecSchema,
} from './block.ts';
import {
  datasetDeclarationSchema,
  observationDatasetSchema,
} from './dataset.ts';
import { jsonObjectSchema, jsonValueSchema } from './json.ts';
import { pageComposeCommandSchema } from './page-composition.ts';
import { visualizationPageSpecSchema } from './page.ts';
import {
  projectMetadataSchema,
  visualizationBriefSchema,
  visualizationProjectSpecSchema,
} from './project.ts';
import { runtimeProjectSpecSchema } from './runtime.ts';
import { sceneCommandSchema } from './scene-commands.ts';
import { validationIssueSchema } from './validation.ts';
import { workflowEventSchema } from './workflow.ts';

export const conversationMessageSchema = z.object({
  id: z.string().min(1),
  turnId: z.string().min(1),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string(),
  createdAt: z.string().datetime(),
}).strict();

const assistantConversationMessageSchema = conversationMessageSchema.extend({
  role: z.literal('assistant'),
});

export const projectCommandSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('project.touch'),
  }).strict(),
  z.object({
    type: z.literal('project.replace'),
    project: visualizationProjectSpecSchema,
  }).strict(),
  z.object({
    type: z.literal('project.metadata.update'),
    title: z.string().optional(),
    metadata: projectMetadataSchema.partial().optional(),
  }).strict(),
  z.object({
    type: z.literal('project.brief.update'),
    brief: visualizationBriefSchema.partial(),
  }).strict(),
  z.object({
    type: z.literal('pedagogy.update'),
    value: jsonObjectSchema.nullable(),
  }).strict(),
  z.object({
    type: z.literal('page.upsert'),
    page: visualizationPageSpecSchema,
  }).strict(),
  pageComposeCommandSchema,
  z.object({
    type: z.literal('page.remove'),
    pageId: z.string().min(1),
  }).strict(),
  z.object({
    type: z.literal('page.patch'),
    pageId: z.string().min(1),
    title: z.string().optional(),
    layout: visualizationPageSpecSchema.shape.layout.optional(),
  }).strict(),
  z.object({
    type: z.literal('page.reorder'),
    pageIds: z.array(z.string().min(1)).max(100),
  }).strict(),
  z.object({
    type: z.literal('block.upsert'),
    pageId: z.string().min(1),
    block: visualizationBlockSpecSchema,
  }).strict(),
  z.object({
    type: z.literal('block.remove'),
    pageId: z.string().min(1),
    blockId: z.string().min(1),
  }).strict(),
  z.object({
    type: z.literal('block.config.patch'),
    pageId: z.string().min(1),
    blockId: z.string().min(1),
    config: jsonObjectSchema,
  }).strict(),
  z.object({
    type: z.literal('scene.commands'),
    pageId: z.string().min(1),
    blockId: z.string().min(1),
    baseSceneRevision: z.number().int().nonnegative(),
    commands: z.array(sceneCommandSchema).min(1).max(1000),
  }).strict(),
  z.object({
    type: z.literal('block.placement.set'),
    pageId: z.string().min(1),
    blockId: z.string().min(1),
    placement: blockPlacementSchema,
  }).strict(),
  z.object({
    type: z.literal('block.bindings.set'),
    pageId: z.string().min(1),
    blockId: z.string().min(1),
    bindings: z.array(dataBindingSchema),
  }).strict(),
  z.object({
    type: z.literal('block.interactions.set'),
    pageId: z.string().min(1),
    blockId: z.string().min(1),
    interactions: z.array(interactionBindingSchema),
  }).strict(),
  z.object({
    type: z.literal('block.move'),
    pageId: z.string().min(1),
    blockId: z.string().min(1),
    targetPageId: z.string().min(1),
    index: z.number().int().nonnegative(),
    placement: blockPlacementSchema.optional(),
  }).strict(),
  z.object({
    type: z.literal('runtime.set'),
    runtime: runtimeProjectSpecSchema,
  }).strict(),
  z.object({
    type: z.literal('dataset.upsert'),
    dataset: datasetDeclarationSchema,
  }).strict(),
  z.object({
    type: z.literal('dataset.remove'),
    datasetId: z.string().min(1),
  }).strict(),
  z.object({
    type: z.literal('static-dataset.upsert'),
    dataset: observationDatasetSchema,
  }).strict(),
  z.object({
    type: z.literal('static-dataset.remove'),
    datasetId: z.string().min(1),
  }).strict(),
  z.object({
    type: z.literal('artifact.upsert'),
    artifact: artifactManifestEntrySchema,
  }).strict(),
  z.object({
    type: z.literal('artifact.attach'),
    artifact: artifactManifestEntrySchema,
  }).strict(),
  z.object({
    type: z.literal('artifact.remove'),
    artifactId: z.string().min(1),
  }).strict(),
]);

const eventBase = {
  eventId: z.string().min(1),
  turnId: z.string().min(1),
  createdAt: z.string().datetime(),
};

export const agentActivityStatusSchema = z.enum([
  'idle',
  'analyzing',
  'awaiting_user',
  'applying',
  'validating',
  'completed',
  'error',
]);

export const visualizationAgentEventSchema = z.discriminatedUnion('type', [
  z.object({
    ...eventBase,
    type: z.literal('assistant.message.delta'),
    messageId: z.string().min(1),
    delta: z.string(),
  }).strict(),
  z.object({
    ...eventBase,
    type: z.literal('assistant.message.completed'),
    message: assistantConversationMessageSchema,
  }).strict(),
  workflowEventSchema,
  z.object({
    ...eventBase,
    type: z.literal('project.commands'),
    projectId: z.string().min(1),
    baseRevision: z.number().int().nonnegative(),
    commands: z.array(projectCommandSchema).min(1),
  }).strict(),
  z.object({
    ...eventBase,
    type: z.literal('validation.result'),
    scope: z.enum(['design', 'project']),
    designRevision: z.number().int().nonnegative(),
    projectRevision: z.number().int().nonnegative(),
    valid: z.boolean(),
    issues: z.array(validationIssueSchema),
  }).strict(),
  z.object({
    ...eventBase,
    type: z.literal('agent.status'),
    status: agentActivityStatusSchema,
  }).strict(),
  z.object({
    ...eventBase,
    type: z.enum([
      'turn.started',
      'context.preparing',
      'provider.started',
      'provider.completed',
      'validation.started',
      'validation.completed',
      'review.started',
      'review.completed',
      'runtime.started',
      'runtime.completed',
      'runtime.failed',
      'tool.started',
      'tool.completed',
      'plan.updated',
      'budget.warning',
      'turn.completed',
    ]),
    sequence: z.number().int().nonnegative().optional(),
    status: z.enum(['started', 'progress', 'completed', 'failed']).optional(),
    operation: z.string().optional(),
    summary: z.string().optional(),
    tool: z.string().optional(),
    callId: z.string().optional(),
    durationMs: z.number().int().nonnegative().optional(),
    plan: z.record(z.string(), jsonValueSchema).optional(),
    result: jsonValueSchema.optional(),
    diagnostics: z.array(validationIssueSchema).optional(),
    valid: z.boolean().optional(),
    metrics: z.record(z.string(), jsonValueSchema).optional(),
  }).passthrough(),
  z.object({
    ...eventBase,
    type: z.literal('error'),
    code: z.string().min(1),
    message: z.string().min(1),
    recoverable: z.boolean().default(true),
  }).strict(),
]);

export type ConversationMessage = z.infer<typeof conversationMessageSchema>;
export type ProjectCommand = z.infer<typeof projectCommandSchema>;
export type AgentActivityStatus = z.infer<typeof agentActivityStatusSchema>;
export type VisualizationAgentEvent = z.infer<
  typeof visualizationAgentEventSchema
>;
